import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AuthBrandPanel, BrandMark } from './AuthBrandPanel';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const manifestoSections = [
  {
    tone: 'accent',
    content: 'We believe compliance should feel like progress, not punishment.',
  },
  {
    tone: 'default',
    content: "That the best risk teams don't just react, they anticipate, support, and lead.",
  },
  {
    tone: 'default',
    content:
      'And that the people protecting customers should be as celebrated as the ones acquiring them.',
  },
  {
    tone: 'accent',
    content:
      "We're building GO, a new layer of intelligence that helps you detect harm, flag vulnerability, and surface unfairness before it becomes formal, financial, or reputational risk.",
  },
  {
    tone: 'default',
    content:
      "We make it easy to see what's happening, simple to escalate what matters, and possible to prove you've done the right thing, without having to explain it ten times over.",
  },
  {
    tone: 'default',
    content:
      "We don't just help you stay compliant. We help you stay ahead, with clarity, confidence, and control.",
  },
  {
    tone: 'accent',
    content:
      "Because in an industry that talks about good outcomes, it's time someone built GO, a platform that actually delivers them.",
  },
];

const Manifesto: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white lg:flex">
      <AuthBrandPanel />

      <div className="flex-1 bg-figma-bg-main">
        <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 lg:hidden">
              <BrandMark />
              <div>
                <p className="text-lg font-semibold text-figma-text-primary">Good Outcomes</p>
                <p className="text-sm text-figma-text-secondary">Manifesto</p>
              </div>
            </Link>

            <div className="ml-auto flex items-center gap-3">
              <Button asChild variant="ghost" className="text-primary hover:bg-primary/5">
                <Link to="/">Home</Link>
              </Button>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </div>
          </div>

          <div className="my-auto space-y-6 py-8">
            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="space-y-3">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">
                  Manifesto
                </p>
                <CardTitle className="text-3xl font-semibold text-foreground sm:text-4xl">
                  The standard we are building to.
                </CardTitle>
                <p className="text-base leading-7 text-muted-foreground">
                  This is the foundation for how we speak, build, and show up for the
                  teams doing the work of protecting customers.
                </p>
              </CardHeader>
            </Card>

            <Card className="border-border bg-card shadow-sm">
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {manifestoSections.map((section) => (
                    <div key={section.content} className="px-6 py-6 sm:px-8">
                      <p
                        className={
                          section.tone === 'accent'
                            ? 'text-xl font-medium leading-8 text-primary sm:text-2xl sm:leading-9'
                            : 'text-xl leading-8 text-foreground sm:text-2xl sm:leading-9'
                        }
                      >
                        {section.content}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="space-y-3">
                <CardTitle className="text-2xl font-semibold text-foreground">
                  Built by the team behind DataEQ. Built for Consumer Duty.
                </CardTitle>
                <p className="text-base leading-7 text-muted-foreground">
                  If you already have access, sign in to continue. If not, start with the
                  beta entry and see how Good Outcomes is taking shape.
                </p>
              </CardHeader>
              <CardContent>
                <Button asChild size="lg">
                  <Link to="/auth" className="inline-flex items-center gap-2">
                    Sign in to the beta
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manifesto;
